import React from 'react';
import { MenuListProps, GroupBase } from 'react-select';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

interface SelectOption {
  value: string;
  label: string;
}

interface MenuListCustomProps {
  maxHeight?: number;
}

const OPTION_HEIGHT = 35;
const DEFAULT_MAX_HEIGHT = 150;

// Custom MenuList component that uses react-window for virtualization
const MenuList = React.forwardRef<HTMLDivElement, MenuListProps<SelectOption, true, GroupBase<SelectOption>> & MenuListCustomProps>(
  ({ children, getValue, maxHeight = DEFAULT_MAX_HEIGHT }, ref) => {
    // Get the currently selected value(s)
    const [value] = getValue();

    // Cast children to array of React elements
    const childrenArray = Array.isArray(children)
      ? children
      : React.Children.toArray(children);

    // Find the index of the selected option
    let initialScrollIndex = 0;
    if (value) {
      for (let i = 0; i < childrenArray.length; i++) {
        const child = childrenArray[i] as React.ReactElement;
        const childProps = child.props as { data?: { value?: string } };
        if (childProps?.data?.value === value.value) {
          initialScrollIndex = i;
          break;
        }
      }
    }

    const initialOffset = initialScrollIndex * OPTION_HEIGHT;
    const itemCount = childrenArray.length;
    const height = Math.min(maxHeight, itemCount * OPTION_HEIGHT);

    // Force type assertion to work around the type issues
    const ListComponent = FixedSizeList as unknown as React.ComponentType<{
      width: string | number;
      height: number;
      itemCount: number;
      itemSize: number;
      overscanCount: number;
      initialScrollOffset: number;
      children: React.ComponentType<ListChildComponentProps>;
    }>;

    return (
      <div ref={ref}>
        <ListComponent
          width="100%"
          height={height}
          itemCount={itemCount}
          itemSize={OPTION_HEIGHT}
          overscanCount={5}
          initialScrollOffset={initialOffset}
        >
          {({ index, style }: ListChildComponentProps): React.ReactElement => (
            <div style={style}>
              {childrenArray[index as number]}
            </div>
          )}
        </ListComponent>
      </div>
    );
  },
);

MenuList.displayName = 'MenuList';

export default MenuList;
